import React, { useEffect, useRef, useState, useCallback } from "react";

import { Button, Field, Input, Popover, Portal } from "@chakra-ui/react";
import { useAppStore } from "../../../context/store";
import { Tooltip } from "../../ui/tooltip";
import Type from "../../../utilies/Types";
import { LuTangent } from "react-icons/lu";
import { setupClickHandler } from "../../../utilies/ArcUtility";
import { serviceGenerateArc } from "../../../api/services/arc";
import { createUniqueId, getGeometryTypeGeojson, showToast, getUsernameAndUserIdFormat } from "../../../utilies/Utility";
import { getEditedGeoJSON } from "../../../utilies/geojson/GetEditedGeojson";
import { loadGeoJSONToCesium } from "../../../utilies/geojson/LoadGeojson";
import { useMapObjects } from "../../../context/MapObjectsContext";
function Arc() {
  const viewerRef = useAppStore((s) => s.viewerRef);
  const { mapObjects, setMapObjects } = useMapObjects();
  const generateArc = useAppStore((s) => s.generateArc);
  const defaultGeojsonStyle = useAppStore((s) => s.defaultGeojsonStyle);
  const editMode = useAppStore((s) => s.editMode);
  const userData = useAppStore((s) => s.userData);
  const setGenerateArc = useAppStore.getState().setGenerateArc;

  const escOffRef = useRef(false);

  const exitArcMode = useCallback(() => {
    if (escOffRef.current) return;
    escOffRef.current = true;
    setGenerateArc(false);
    setTimeout(() => {
      escOffRef.current = false;
    }, 0);
  }, [setGenerateArc]);

  const [isShow, stIsShow] = useState(false);
  const [data, setData] = useState({
    curvature: 0.8,
    start_left_offset: 0.5,
    start_right_offset: 0.5,
  });
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = {
      curvature: parseFloat(data.curvature),
      start_left_offset: parseFloat(data.start_left_offset),
      start_right_offset: parseFloat(data.start_right_offset),
    };
  }, [data]);

  useEffect(() => {
    // Flags to track existence of layers
    let hasLanelet = false;
    let hasTurningLane = false;

    for (const key in mapObjects) {
      const fileName = mapObjects[key].file_name.toLowerCase(); // normalize case

      if (fileName.includes("lanelet")) {
        hasLanelet = true; // found lanelet
      }
      if (fileName.includes("turning_line")) {
        hasTurningLane = true; // found turning_line
      }

      // If both found, no need to continue
      if (hasLanelet && hasTurningLane) {
        break;
      }
    }

    // Final result
    const result = hasLanelet && hasTurningLane;
    stIsShow(result);
  }, [mapObjects]);

  useEffect(() => {
    if (!generateArc) return;

    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      exitArcMode();
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });

    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [generateArc, exitArcMode]);

  useEffect(() => {
    let handler;

    if (generateArc) {
      showToast("Select Lanelet", Type.NOTIFICATION.INFO);
      handler = setupClickHandler(viewerRef.current, (laneletId) => {
        setGenerateArc(false);
        let laneletLayerId = null;
        let turningLineLayerId = null;
        for (const key in mapObjects) {
          if (mapObjects[key].file_name.toLowerCase().includes("lanelet")) {
            laneletLayerId = mapObjects[key].layer_id;
          }
          if (mapObjects[key].file_name.toLowerCase().includes("turning_line")) {
            turningLineLayerId = mapObjects[key].layer_id;
          }
        }
        serviceGenerateArc({
          lanelet_layer_id: laneletLayerId,
          turning_line_layer_id: turningLineLayerId,
          lanelet_id: laneletId,
          data: dataRef.current,
        })
          .then((res) => {
            if (res.data.status == "success") {
              loadGeojson(res.data.geojson);
            } else {
              showToast(res.data.message, Type.NOTIFICATION.WARNING);
            }
          })
          .catch((err) => {
            console.error("Error", err);
            showToast(err.message, Type.NOTIFICATION.ERROR);
          });
      });
    }

    return () => {
      if (handler) {
        handler.destroy();
      }
    };
  }, [generateArc]);

  const loadGeojson = (geojson) => {
    const creator = getUsernameAndUserIdFormat(userData);
    const geojsonWithCreator = {
      ...geojson,
      features: (geojson?.features || []).map((feature) => ({
        ...feature,
        properties: {
          ...(feature?.properties || {}),
          ...(creator ? { creator } : {}),
        },
      })),
    };
    let isExistTurningArc = false;
    let temp = [...mapObjects];
    for (const key in temp) {
      if (temp[key].file_name.toLowerCase().includes("turning_arc")) {
        let lastGeojson = getEditedGeoJSON(temp[key]);
        lastGeojson.features = [...lastGeojson.features, ...geojsonWithCreator.features];
        temp[key].dataSource.load(lastGeojson);
        isExistTurningArc = true;
        temp[key].is_edited = true;
        break;
      }
    }
    setMapObjects(temp);

    if (!isExistTurningArc) {
      createNewFile(geojsonWithCreator);
    }
  };

  function insertByPriority(list, item) {
    // Normalize priority: default to 0 if missing/invalid
    const itemPriority = Number.isFinite(Number(item.priority)) ? Number(item.priority) : 0;

    // Stable tie-breaker: earlier items stay "lower" if same priority
    if (typeof item._priorityOrder !== "number") {
      item._priorityOrder = typeof performance !== "undefined" ? performance.now() : Date.now();
    }

    // Find correct insertion index:
    // - If priority equal, sort by _priorityOrder ASC (older first)
    let idx = list.length; // default append at end
    for (let i = 0; i < list.length; i++) {
      const cur = list[i];
      const curPriority = Number.isFinite(Number(cur.priority)) ? Number(cur.priority) : 0;
      const curOrder = Number.isFinite(Number(cur._priorityOrder)) ? Number(cur._priorityOrder) : 0;

      if (curPriority < itemPriority) {
        idx = i;
        break;
      } else if (curPriority === itemPriority) {
        if (curOrder > item._priorityOrder) {
          idx = i;
          break;
        }
      }
    }

    // Return a NEW array with the item inserted
    const out = list.slice(0, idx);
    out.push(item);
    return out.concat(list.slice(idx));
  }

  const createNewFile = (geojsonData) => {
    const id = createUniqueId();
    setMapObjects((prev) => {
      const base = {
        id,
        name: "Geojson",
        entities: [],
        type: Type.ASSET.GEOJSON,
        file_name: "TURNING_ARC.geojson",
        file_type: ".geojson",
        inner_entities: [],
        visible: true,
        dataSource: null,
        label_visible: false,
        geojson: geojsonData,
        view_type: "line",
        osm_data: null,
        osmDataSource: null,
        style_stroke_width: null,
        style_fill_color: null,
        style_outline_color: null,
        style_type: null,
        style_pattern: "solid",
        is_center_lane: false,
        auto_select: false,
        show_lanelet: false,
        priority: 0,
        is_edited: true,
        _priorityOrder: performance?.now?.() ?? Date.now(),
        geojson_type: getGeometryTypeGeojson(geojsonData),
      };

      const next = insertByPriority(prev, base);

      loadGeoJSONToCesium({
        viewer: viewerRef.current,
        geojson: geojsonData,
        options: { ...defaultGeojsonStyle },
        callback: (dataSource, geoJson, billboards, fill, outline, width, type) => {
          setMapObjects((cur) =>
            cur.map((o) =>
              o.id === id
                ? {
                    ...o,
                    dataSource,
                    billboards,
                    style_stroke_width: width,
                    style_fill_color: fill,
                    style_outline_color: outline,
                    style_type: type,
                  }
                : o,
            ),
          );
        },
        geojsonType: Type.ASSET.GEOJSON,
        layerName: "TURNING_ARC.geojson",
        id,
      });

      return next;
    });
  };

  return (
    <>
      {isShow && (
        <Popover.Root positioning={{ placement: "left-start" }}>
          <Popover.Trigger asChild>
            <Button
              disabled={editMode}
              variant="solid"
              p={0}
              opacity={1}
              bg={generateArc ? "green" : "white"}
              onClick={() => {
                setGenerateArc(!generateArc);
              }}
              color={generateArc ? "white" : "MenuText"}
            >
              <Tooltip content="Generate Arc">
                <LuTangent />
              </Tooltip>
            </Button>
          </Popover.Trigger>
          <Portal>
            <Popover.Positioner>
              <Popover.Content width={"fit-content"}>
                <Popover.Arrow />
                <Popover.Body p={2}>
                  <Field.Root>
                    <Field.Label>Curvature</Field.Label>
                    <Input
                      placeholder="curvature"
                      value={data.curvature}
                      onChange={(v) => {
                        setData((prev) => ({
                          ...prev,
                          curvature: v.target.value,
                        }));
                      }}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Start Left Offset</Field.Label>
                    <Input
                      placeholder="start_left_offset"
                      value={data.start_left_offset}
                      onChange={(v) => {
                        setData((prev) => ({
                          ...prev,
                          start_left_offset: v.target.value,
                        }));
                      }}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Start Right Offset</Field.Label>
                    <Input
                      placeholder="start_right_offset"
                      value={data.start_right_offset}
                      onChange={(v) => {
                        setData((prev) => ({
                          ...prev,
                          start_right_offset: v.target.value,
                        }));
                      }}
                    />
                  </Field.Root>
                </Popover.Body>
              </Popover.Content>
            </Popover.Positioner>
          </Portal>
        </Popover.Root>
      )}
    </>
  );
}

export default Arc;
